/**
 * Returns an rgba background color on a continuous green-yellow-orange-red-purple spectrum based on FDI value.
 *
 * FDI thresholds (AFDRS):
 *   0–11  → green  (No Rating)
 *  12–23  → yellow (Moderate)
 *  24–49  → orange (High)
 *  50–99  → red (Extreme)
 *  100+   → purple (Catastrophic)
 */
export const getFDIColor = (fdi: number, alpha = 0.85): string => {
  // Color stops: green(0) → yellow(12) → orange(24) → red(50) → purple(100)
  // Brighter, more saturated colors
  // Green:  rgb(76, 175, 80)   - #4CAF50
  // Yellow: rgb(255, 235, 59)  - #FFEB3B
  // Orange: rgb(255, 152, 0)   - #FF9800
  // Red:    rgb(244, 67, 54)   - #F44336
  // Purple: rgb(156, 39, 176)  - #9C27B0

  const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);

  let r: number, g: number, b: number;

  if (fdi <= 0) {
    // Green
    r = 76; g = 175; b = 80;
  } else if (fdi <= 12) {
    // Green → Yellow
    const t = fdi / 12;
    r = lerp(76, 255, t);
    g = lerp(175, 235, t);
    b = lerp(80, 59, t);
  } else if (fdi <= 24) {
    // Yellow → Orange
    const t = (fdi - 12) / 12;
    r = lerp(255, 255, t);
    g = lerp(235, 152, t);
    b = lerp(59, 0, t);
  } else if (fdi <= 50) {
    // Orange → Red
    const t = (fdi - 24) / 26;
    r = lerp(255, 244, t);
    g = lerp(152, 67, t);
    b = lerp(0, 54, t);
  } else if (fdi <= 100) {
    // Red → Purple
    const t = (fdi - 50) / 50;
    r = lerp(244, 156, t);
    g = lerp(67, 39, t);
    b = lerp(54, 176, t);
  } else {
    // Purple (capped)
    r = 156; g = 39; b = 176;
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
